import { useStore } from "hooks/use-store";
import { useTranslation } from "react-i18next";
import { Color } from "utils/theme";
import classNames from "classnames";
import { Screens } from "navigation/routes";
import { Compass, Gauge, Settings, Siren } from "lucide-react";

interface Props {
  showMenu: boolean;
  setShowMenu: (show: boolean) => void;
}

interface MenuItemProps {
  onClick: () => void;
  icon?: JSX.Element;
  label: string;
  isActive?: boolean;
  badge?: number | null;
}

const MenuItem = ({
  onClick,
  icon,
  label,
  badge = null,
  isActive,
}: MenuItemProps) => {
  return (
    <button className="flex items-center mb-6" onClick={onClick}>
      <span className={"relative flex items-center rounded-xl w-14 h-14"}>
        <div
          className={classNames("w-full h-full p-3 rounded-xl", {
            "text-white": isActive,
            "text-primary-500": !isActive,
          })}
          style={{
            background: isActive ? Color.Primary[700] : "none",
            stroke: isActive ? Color.White : Color.Primary[500],
          }}
        >
          {icon}
        </div>
        {badge && badge > 0 ? (
          <div className="absolute top-0 right-0 w-6 h-6 rounded-full flex items-center justify-center bg-rose-500">
            <p className="text-xs font-bold">{badge > 99 ? "99+" : badge}</p>
          </div>
        ) : null}
      </span>
      <p
        className={"text-2xl ml-4 font-medium"}
        style={{
          color: isActive ? Color.White : Color.Primary[500],
        }}
      >
        {label}
      </p>
    </button>
  );
};

export const Drawer = ({
  showMenu,
  setShowMenu,
}: Props): JSX.Element | null => {
  const { t } = useTranslation("translation", {
    keyPrefix: "drawer",
  });
  const alertCounter = useStore((state) => state.alertCounter);
  const screen = useStore((state) => state.screen);
  const setScreen = useStore((state) => state.setScreen);

  const closeDrawer = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setShowMenu(false);
  };

  if (!showMenu) return null;

  return (
    <div
      onClick={(e) => closeDrawer(e)}
      className="absolute top-0 left-0 h-screen overflow-auto w-full shadow-lg flex flex-col py-20 px-4 z-40"
      style={{ background: Color.Primary[900] }}
    >
      <MenuItem
        onClick={() => setScreen(Screens.Dashboard)}
        isActive={screen === Screens.Dashboard}
        label={t("dashboard")}
        icon={<Gauge className="w-8 h-8" />}
      />
      <MenuItem
        onClick={() => setScreen(Screens.Info)}
        isActive={screen === Screens.Info}
        label={t("trip_info")}
        icon={<Compass className="w-8 h-8" />}
      />
      <MenuItem
        onClick={() => setScreen(Screens.Alerts)}
        isActive={screen === Screens.Alerts}
        label={t("alerts")}
        badge={alertCounter}
        icon={<Siren className="w-8 h-8" />}
      />
      <MenuItem
        onClick={() => setScreen(Screens.Settings)}
        isActive={screen === Screens.Settings}
        label={t("settings")}
        icon={<Settings className="w-8 h-8" />}
      />
    </div>
  );
};
